import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, ShieldCheck, UserPlus, Trash2, Key, ArrowLeft, Eye, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const UNLOCK_KEY = "admin_control_unlock_token";
const UNLOCK_EXPIRES_KEY = "admin_control_unlock_expires";

/**
 * Admin Control - password-gated super-panel. Replaces the legacy Admin
 * Dashboard link from Naomi's /me. Controls:
 *  - Admin accounts (grant/revoke, per-tab permissions)
 *  - Password rotation
 *  - Quick jumps to other admin tools (copy editor, platform settings,
 *    category master, switchboard)
 */
export default function AdminControlPage() {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [unlocked, setUnlocked] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [admins, setAdmins] = useState([]);
  const [rotateModal, setRotateModal] = useState(false);
  const [rotateForm, setRotateForm] = useState({ current: "", new: "", confirm: "" });
  const [addAdminForm, setAddAdminForm] = useState({ user_id: "", tabs: [] });

  useEffect(() => {
    const token = localStorage.getItem(UNLOCK_KEY);
    const expires = parseInt(localStorage.getItem(UNLOCK_EXPIRES_KEY) || "0", 10);
    if (token && expires > Date.now()) {
      setUnlocked(true);
    }
  }, []);

  const loadAdmins = async () => {
    const token = localStorage.getItem("session_token");
    try {
      const res = await fetch(`${API}/api/admin/control/admins`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setAdmins(data.admins || []);
    } catch (err) {
      toast.error("Load admins failed: " + err.message);
    }
  };

  useEffect(() => { if (unlocked) loadAdmins(); }, [unlocked]);

  const verify = async (e) => {
    e.preventDefault();
    setVerifying(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/api/admin/control/verify-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ password }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || "Verification failed");
      }
      const d = await res.json();
      localStorage.setItem(UNLOCK_KEY, d.unlock_token);
      localStorage.setItem(UNLOCK_EXPIRES_KEY, String(Date.now() + d.expires_in * 1000));
      setUnlocked(true);
      setPassword("");
      toast.success("Unlocked");
    } catch (err) {
      toast.error(String(err.message || err));
    } finally {
      setVerifying(false);
    }
  };

  const rotate = async (e) => {
    e.preventDefault();
    if (rotateForm.new !== rotateForm.confirm) {
      toast.error("New password mismatch");
      return;
    }
    if (rotateForm.new.length < 12) {
      toast.error("Password must be 12+ chars");
      return;
    }
    const token = localStorage.getItem("session_token");
    try {
      const res = await fetch(`${API}/api/admin/control/rotate-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ current: rotateForm.current, new: rotateForm.new }),
      });
      if (!res.ok) throw new Error(await res.text());
      toast.success("Password rotated. Save the new one.");
      setRotateModal(false);
      setRotateForm({ current: "", new: "", confirm: "" });
      // Clear unlock so user must re-verify
      localStorage.removeItem(UNLOCK_KEY);
      localStorage.removeItem(UNLOCK_EXPIRES_KEY);
      setUnlocked(false);
    } catch (err) {
      toast.error(String(err.message || err));
    }
  };

  const removeAdmin = async (userId) => {
    if (!window.confirm("Revoke admin from this user?")) return;
    const token = localStorage.getItem("session_token");
    try {
      const res = await fetch(`${API}/api/admin/control/admins/${userId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(await res.text());
      toast.success("Admin revoked");
      loadAdmins();
    } catch (err) {
      toast.error(String(err.message || err));
    }
  };

  const addAdmin = async () => {
    if (!addAdminForm.user_id) {
      toast.error("user_id required");
      return;
    }
    const token = localStorage.getItem("session_token");
    try {
      const res = await fetch(`${API}/api/admin/control/admins`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ user_id: addAdminForm.user_id, admin_tab_permissions: addAdminForm.tabs }),
      });
      if (!res.ok) throw new Error(await res.text());
      toast.success("Admin added");
      setAddAdminForm({ user_id: "", tabs: [] });
      loadAdmins();
    } catch (err) {
      toast.error(String(err.message || err));
    }
  };

  if (!unlocked) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4" data-testid="admin-control-gate">
        <form onSubmit={verify} className="bg-white rounded-xl p-6 w-full max-w-md shadow-lg">
          <div className="flex items-center gap-2 mb-4">
            <Lock className="w-5 h-5 text-gray-700" />
            <h1 className="text-lg font-semibold">Admin Control</h1>
          </div>
          <p className="text-sm text-gray-500 mb-4">
            This area is password-protected. Enter the Admin Control password to continue.
          </p>
          <Input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Admin Control password"
            className="mb-3"
            autoFocus
            required
            data-testid="admin-control-password-input"
          />
          <Button type="submit" className="w-full" disabled={verifying} data-testid="admin-control-unlock-btn">
            {verifying ? <Loader2 className="w-4 h-4 animate-spin" /> : "Unlock"}
          </Button>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="w-full mt-2 text-xs text-gray-500 hover:text-gray-700"
          >
            Back
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-control-page">
      <header className="bg-white border-b border-gray-200 px-4 sm:px-6 py-4 sticky top-0 z-20">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button size="sm" variant="ghost" onClick={() => navigate(-1)} data-testid="admin-control-back">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <ShieldCheck className="w-5 h-5 text-indigo-600" />
            <h1 className="text-lg font-semibold">Admin Control</h1>
          </div>
          <Button size="sm" variant="outline" onClick={() => setRotateModal(true)} data-testid="admin-control-rotate-password-btn">
            <Key className="w-3.5 h-3.5 mr-1" /> Rotate password
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-4">
        {/* Quick jumps */}
        <section className="bg-white rounded-xl p-4">
          <h2 className="font-semibold text-sm mb-3">Quick jumps</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate("/admin/category-master")} data-testid="jump-category-master">Category Master</Button>
            <Button variant="outline" size="sm" onClick={() => navigate("/admin/copy-editor")} data-testid="jump-copy-editor">Copy Editor</Button>
            <Button variant="outline" size="sm" onClick={() => navigate("/admin/platform-settings")} data-testid="jump-platform-settings">Platform Settings</Button>
            <Button variant="outline" size="sm" onClick={() => navigate("/admin/advocates")} data-testid="jump-advocates">Advocates</Button>
            <Button variant="outline" size="sm" onClick={() => navigate("/admin/audit?filter=switchboard")} data-testid="jump-audit">Audit Trail</Button>
            <Button variant="outline" size="sm" onClick={() => navigate("/admin/launch-control")} data-testid="jump-launch-control">Launch Control</Button>
          </div>
        </section>

        {/* Admin users */}
        <section className="bg-white rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold text-sm">Admin users</h2>
            <span className="text-xs text-gray-500">{admins.length}</span>
          </div>
          {admins.length === 0 ? (
            <p className="text-sm text-gray-500">No admins.</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {admins.map((a) => (
                <li key={a.user_id} className="py-2.5 flex items-center justify-between" data-testid={`admin-row-${a.user_id}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{a.name || a.email || a.user_id}</p>
                    <p className="text-xs text-gray-500 truncate">
                      {a.email} · {a.is_super_admin ? "Super Admin" : "Admin"}
                      {a.admin_tab_permissions?.length > 0 && ` · ${a.admin_tab_permissions.join(", ")}`}
                    </p>
                  </div>
                  {!a.is_super_admin && (
                    <Button size="sm" variant="ghost" onClick={() => removeAdmin(a.user_id)} className="text-red-600" data-testid={`admin-remove-btn-${a.user_id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}

          {/* Add admin form */}
          <div className="mt-3 pt-3 border-t border-gray-100">
            <p className="text-xs font-medium text-gray-700 mb-2">Grant admin to a user</p>
            <div className="flex gap-2">
              <Input
                value={addAdminForm.user_id}
                onChange={(e) => setAddAdminForm({ ...addAdminForm, user_id: e.target.value })}
                placeholder="usr_..."
                className="text-sm"
                data-testid="admin-add-user-id-input"
              />
              <Button size="sm" onClick={addAdmin} data-testid="admin-add-submit-btn">
                <UserPlus className="w-3.5 h-3.5 mr-1" /> Add
              </Button>
            </div>
            <p className="text-xs text-gray-400 mt-1">Tab permissions editor coming next pass (today: full access).</p>
          </div>
        </section>
      </main>

      {/* Rotate password modal */}
      {rotateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="rotate-password-modal">
          <form onSubmit={rotate} className="bg-white rounded-xl p-5 w-full max-w-sm">
            <h3 className="font-semibold mb-3">Rotate Admin Control password</h3>
            <div className="space-y-2">
              <Input type="password" placeholder="Current password" value={rotateForm.current}
                     onChange={(e) => setRotateForm({ ...rotateForm, current: e.target.value })}
                     required data-testid="rotate-current-input" />
              <Input type="password" placeholder="New password (12+ chars)" value={rotateForm.new}
                     onChange={(e) => setRotateForm({ ...rotateForm, new: e.target.value })}
                     required data-testid="rotate-new-input" />
              <Input type="password" placeholder="Confirm new password" value={rotateForm.confirm}
                     onChange={(e) => setRotateForm({ ...rotateForm, confirm: e.target.value })}
                     required data-testid="rotate-confirm-input" />
            </div>
            <div className="flex gap-2 mt-4">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setRotateModal(false)}>Cancel</Button>
              <Button type="submit" className="flex-1" data-testid="rotate-submit-btn">Rotate</Button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
